<script setup lang="ts">
import type { ThreadSummary } from '../stores/threads'
import { formatDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'

// The Ask drawer's "Threads" list, shown when no thread is open: each row
// is the thread title over its turn count and last-activity time. The
// relative time re-renders off useNow's ticking clock, no refetch.
defineProps<{
  threads: readonly ThreadSummary[]
}>()

const emit = defineEmits<{
  (e: 'open', id: number): void
}>()

const now = useNow()

function meta(t: ThreadSummary): string {
  const turns = `${t.turn_count} turn${t.turn_count === 1 ? '' : 's'}`
  const when = t.last_message_at ? formatDate(t.last_message_at, now.value) : 'new'
  return `${turns} · ${when}`
}
</script>

<template>
  <section class="flex flex-col gap-2" aria-labelledby="thread-recent-heading" data-testid="thread-recent">
    <h3
      id="thread-recent-heading"
      class="m-0 px-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint"
    >
      Threads
    </h3>
    <ul class="m-0 flex list-none flex-col gap-0.5 p-0">
      <li v-for="t in threads" :key="t.id">
        <button
          type="button"
          class="flex min-h-11 w-full cursor-pointer flex-col gap-0.5 rounded-lg border-0 bg-transparent px-2.5 py-2 text-left text-ink-muted hover:bg-surface-hover hover:text-ink"
          data-testid="thread-recent-item"
          @click="emit('open', t.id)"
        >
          <span class="w-full truncate">{{ t.title }}</span>
          <span class="text-xs text-ink-faint">{{ meta(t) }}</span>
        </button>
      </li>
    </ul>
  </section>
</template>
